import { ChatWorkflowRequestBodyDto } from "@intelligems/ig-types/dtos/ai/workflows/chat-workflow";

/**
 * The single React-bound surface of the Viv chat: the streaming-workflow hook's
 * `mutate`/`stop`, which can only be created inside a component
 * (`useChatWorkflowShared`). Everything else about a turn — assembling the
 * request, pending state, continuation orchestration — lives in Redux thunks
 * and listeners that reach this handle through the module ref below.
 */
export interface VivChatWorkflowHandle {
  /** Fire a streaming turn with a fully-assembled request body. */
  mutate: (payload: ChatWorkflowRequestBodyDto) => Promise<void>;
  /**
   * Abort the in-flight stream and settle chat state back to idle. `source`
   * (e.g. "user_stop", "close_builder") is forwarded to the backend `/cancel`
   * so the resulting interruption is attributable in logs.
   */
  stop: (source?: string) => void;
}

// Set by `useVivChatBridge` on mount, cleared on unmount. Its presence is the
// "the chat is open and can run a turn" signal: thunks read it and no-op when
// it's absent, so a continuation can never fire against a torn-down hook.
let handle: VivChatWorkflowHandle | null = null;

export function setVivChatWorkflowHandle(next: VivChatWorkflowHandle): void {
  handle = next;
}

export function clearVivChatWorkflowHandle(): void {
  handle = null;
}

export function getVivChatWorkflowHandle(): VivChatWorkflowHandle | null {
  return handle;
}
